import { Box, Stack, Typography, Button, ToggleButtonGroup, Tooltip } from "@mui/joy";
import { CalendarToday, FilterList } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import FilterChip from "./FilterChip";
import { HEADER_HEIGHT } from "components/common/Layout";
import { getGlobalPositions, getEmployerPositions, getEmployer } from "apiconfig/api";
import { useSession } from "context/SessionContext";

interface EmployerCandidateListHeaderProps {
  candidateCount: number;
  selectedApplicantsCount: number;
  selectedApplicantIds?: string[];
  hasActiveFilters?: boolean;
  currentZipcode?: string;
  currentPositions?: string;
  currentEmployerPositions?: string;
  currentEmployers?: string[];
  clearSelection?: () => void;
  onClearFilter?: (filterType: 'zipcode' | 'roles' | 'employers' | 'employerPositions') => void;
  onEmployerFilterChange?: (employers: string[]) => void;
  setFilterModalOpen: (open: boolean) => void;
  showEmployerToggle?: boolean;
}

const CandidateListHeader: React.FC<EmployerCandidateListHeaderProps> = ({
  candidateCount,
  selectedApplicantsCount,
  selectedApplicantIds = [],
  hasActiveFilters = false,
  currentZipcode,
  currentPositions,
  currentEmployerPositions,
  currentEmployers,
  clearSelection,
  onClearFilter,
  onEmployerFilterChange,
  setFilterModalOpen,
  showEmployerToggle = false
}) => {
  const navigate = useNavigate();
  const { sessionEmployerId } = useSession();
  const hasSelectedApplicants = selectedApplicantsCount > 0;
  
  const [globalPositions, setGlobalPositions] = useState<{id: string, displayName: string}[]>([]);
  const [employerPositions, setEmployerPositions] = useState<any[]>([]);
  const [currentEmployer, setCurrentEmployer] = useState<{companyName: string} | null>(null);
  const [loadingEmployer, setLoadingEmployer] = useState(false);

  // Fetch positions data and employer info for ID-to-name mapping
  useEffect(() => {
    const fetchPositionsData = async () => {
      try {
        // Fetch global positions
        const globalResponse = await getGlobalPositions();
        setGlobalPositions(globalResponse.data?.globalPositions || []);
        
        // Fetch employer data if sessionEmployerId is available
        if (sessionEmployerId) {
          setLoadingEmployer(true);
          // Fetch employer info
          const employerInfoResponse = await getEmployer(sessionEmployerId);
          setCurrentEmployer(employerInfoResponse.data.employer);
          
          // Fetch employer positions
          const employerResponse = await getEmployerPositions(sessionEmployerId, {
            sort_by: 'displayNameOverride',
            sort_direction: 'ASC'
          }, true);
          setEmployerPositions(employerResponse.data.employerPositions || []);
          setLoadingEmployer(false);
        }
      } catch (err) {
        console.error("Error fetching positions data:", err);
        setLoadingEmployer(false);
      }
    };

    fetchPositionsData();
  }, [sessionEmployerId]);

  // Helper function to map role IDs to display names
  const mapRoleIdsToNames = (roleIds: string) => {
    const ids = roleIds.split(',');
    const names = ids.map(id => {
      const globalPosition = globalPositions.find(pos => pos.id === id);
      return globalPosition?.displayName || '';
    });
    return names.join(', ');
  };

  // Helper function to map employer position IDs to display names
  const mapEmployerPositionIdsToNames = (positionIds: string) => {
    const ids = positionIds.split(',');
    const names = ids.map(id => {
      const employerPosition = employerPositions.find(pos => pos.id === id);
      return employerPosition?.displayNameOverride || employerPosition?.position?.displayName || '';
    });
    return names.join(', ');
  };

  // Helper function to map employer filter values to display names
  const mapEmployerFilterToNames = (employerValues: string[]) => {
    const names = employerValues.map(value => {
      if (value === sessionEmployerId) {
        return currentEmployer?.companyName || 'Current Employer';
      } else if (value === 'other') {
        return 'All Other';
      }
      return value; // fallback to the value itself
    });
    return names.join(', ');
  };
  
  return (
    <Box
      data-header-sticky
      sx={{
        position: 'sticky',
        display: {xs: 'none', sm: 'block'},
        top: HEADER_HEIGHT,
        left: 0,
        right: 0,
        zIndex: 10,
        background: 'linear-gradient(to bottom, rgba(244, 244, 244, 1.0) 0%, rgba(244, 244, 244, 1.0) 50%, rgba(244, 244, 244, 0.0) 100%)',
        my: 4,
        py: 2,
      }}
    >
      <Stack
        direction="column"
        spacing={2}
        sx={{
          py: 2,
          px: 4,
          backgroundColor: 'white',
          borderRadius: '16px',
        }}
      >
        {/* Top row with title and buttons */}
        <Stack
          direction="row"
          spacing={2}
          sx={{
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {/* Employer Filter Toggle */}
          {showEmployerToggle && sessionEmployerId && onEmployerFilterChange && (
            <ToggleButtonGroup
              value={currentEmployers || []}
              onChange={(event, newValue) => {
                onEmployerFilterChange(newValue);
              }}
              disabled={loadingEmployer}
              sx={{
                '& .MuiButton-root': {
                  maxWidth: '120px',
                  minWidth: '80px',
                  px: 2,
                  bgcolor: '#FFFFFF',
                  color: '#1A1B1D',
                  border: '1px solid #D1D5DB',
                  fontWeight: 500,
                  '&:hover': {
                    bgcolor: '#F9FAFB',
                    borderColor: '#9CA3AF',
                  },
                  '&[aria-pressed="true"]': {
                    bgcolor: '#2E2D7E',
                    color: '#FFFFFF',
                    borderColor: '#2E2D7E',
                    '&:hover': {
                      bgcolor: '#1E1D6E',
                      borderColor: '#1E1D6E',
                    },
                  },
                },
              }}
            >
              <Tooltip title={loadingEmployer ? 'Loading...' : (currentEmployer?.companyName || 'Current Employer')} arrow>
                <Button 
                  value={sessionEmployerId} 
                  sx={{ 
                    borderTopRightRadius: 0, 
                    borderBottomRightRadius: 0,
                    display: 'block',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {loadingEmployer ? 'Loading...' : (currentEmployer?.companyName || 'Current Employer')}
                </Button>
              </Tooltip>
              <Tooltip title="All Other" arrow>
                <Button 
                  value="other" 
                  sx={{ 
                    borderTopLeftRadius: 0, 
                    borderBottomLeftRadius: 0,
                    display: 'block',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  All Other
                </Button>
              </Tooltip>
            </ToggleButtonGroup>
          )}
          
          <Typography level="h3" sx={{ flexGrow: 1 }}>
            {candidateCount} {candidateCount !== 1 ? 'People' : 'Person'}
          </Typography>
          <Button 
            variant="plain" 
            size="sm" 
            sx={{ color: "black", p: 2 }}
            onClick={() => setFilterModalOpen(true)}
          >
            <FilterList />
          </Button>
          {hasSelectedApplicants && (
            <Button
              variant="plain"
              size="sm"
              sx={{ color: '#333333', display: { xs: 'none', md: 'block' } }}
              onClick={() => clearSelection?.()}
            >
              Clear Selection
            </Button>
          )}
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            {hasSelectedApplicants ? (
              <Button 
                variant="solid" 
                size="sm" 
                startDecorator={<CalendarToday />}
                sx={{ 
                  backgroundColor: '#2e2d7e',
                  '&:hover': { backgroundColor: '#1e1d6e' }
                }}
                onClick={() => {
                  const applicantIdsParam = selectedApplicantIds.join(',');
                  navigate(`/hiring/candidates/selected?applicantIds=${applicantIdsParam}`);
                }}
              >
                Meet {selectedApplicantsCount} Candidate{selectedApplicantsCount !== 1 ? 's' : ''}
              </Button>
            ) : (
              <Button
                variant="plain"
                size="sm"
                sx={{ color: "black", p: 2 }}>
                <CalendarToday />
              </Button>
            )}
          </Box>
        </Stack>

        {/* Filter chips row */}
        {hasActiveFilters && (
          <Stack
            direction="row"
            spacing={1}
            sx={{
              flexWrap: "wrap",
              gap: 1
            }}
          >
            {currentEmployers && currentEmployers.length > 0 && (
              <FilterChip
                label="Employer"
                value={mapEmployerFilterToNames(currentEmployers)}
                onClear={() => onClearFilter?.('employers')}
              />
            )}
            {currentPositions && (
              <FilterChip
              label="Role"
              value={mapRoleIdsToNames(currentPositions)}
              onClear={() => onClearFilter?.('roles')}
              />
            )}
            {currentZipcode && (
              <FilterChip
                label="Zip Code"
                value={currentZipcode}
                onClear={() => onClearFilter?.('zipcode')}
              />
            )}
            {currentEmployerPositions && (
              <FilterChip
                label={`${currentEmployer?.companyName || 'Company'} Position`}
                value={mapEmployerPositionIdsToNames(currentEmployerPositions)}
                onClear={() => onClearFilter?.('employerPositions')}
              />
            )}
          </Stack>
        )}
      </Stack>
    </Box>
  );
};

export default CandidateListHeader; 